import React from 'react';
import { Box, ButtonBase, CircularProgress } from '@mui/material';
import { postAWSDocument } from './helpers';
import type { PLRDocument } from '.';
import DocumentCard, { chipButtonSx } from './DocumentCard';
import analytics from '../../utils/analytics';
import PlrModal from '../../feature/preloan/v3-redesign/landing/PlrModal';
import { PlrButton } from '../../feature/preloan/v3-redesign/landing/ui';
import { plr } from '../../feature/preloan/v3-redesign/landing/tokens';
import {
  alertCircleIcon, checkCircleIcon, fileTextIcon, uploadIcon,
} from '../../feature/preloan/v3-redesign/landing/icons';

type DocState = {
  file: File | undefined,
  fileOk: boolean,
  pageRefs: React.RefObject<HTMLCanvasElement>[],
  uploaded: boolean,
};

const emptyDoc = (): DocState => ({
  file: undefined, fileOk: false, pageRefs: [], uploaded: false,
});

// Extensión según el tipo del archivo (el nombre puede venir sin extensión o en mayúsculas).
const EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png',
};
const extension = (file?: File) => {
  if (!file) return undefined;
  if (EXTENSIONS[file.type]) return EXTENSIONS[file.type];
  const parts = file.name.split('.');
  return parts[parts.length - 1].toLowerCase();
};

// El backend crea todas las liquidaciones con una sola extensión (payslipExtension), así que
// tienen que venir en el mismo formato que la primera.
const MIXED_FORMAT_ERROR = 'Sube todas las liquidaciones en el mismo formato que la primera (PDF, JPG o PNG).';

// "09/2026": mes anterior al actual menos index (mismos meses que el diálogo de antes).
const pastMonthLabel = (index: number) => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - (index + 1));
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

type UploadDocumentsListProps = {
  needsSettlement: boolean,
  needsPensionStatement: boolean,
  settlementsRequired: number,
  pensionStatementsRequired: number,
  getDocumentsUrl: (payslipExtension?: string, pensionQuoteExtension?: string)
    => Promise<{ data: {
      cotizacionUrl: PLRDocument | undefined,
      liquidacionUrls: PLRDocument[] | undefined,
    } }>,
  // Todos los archivos elegidos y válidos.
  onReadyChange: (ready: boolean) => void,
  // Cambia a true para subir; el padre lo vuelve a false en onUploaded / onUploadError.
  upload: boolean,
  onUploaded: () => void,
  onUploadError: () => void,
  // Primera vez que la persona abre el selector de archivos (evento de analytics del diálogo).
  onFirstChoose?: () => void,
};

// Lista de documentos del rediseño PLR (reemplaza a UploadDocuments + el diálogo de
// liquidaciones): una tarjeta por archivo y la misma subida a S3 (postAWSDocument).
type RowStatus = 'empty' | 'partial' | 'error' | 'ok' | 'uploaded';

// Fila de "Últimas N liquidaciones": las tarjetas van en una hoja aparte para no ocupar
// tanto espacio en mobile (como el diálogo "Subir liquidaciones" del flujo anterior).
function SettlementsRow({
  count, ready, problems, chosen, uploading, uploaded, onOpen,
}: {
  count: number,
  ready: number,
  problems: number,
  chosen: number,
  uploading: boolean,
  uploaded: boolean,
  onOpen: () => void,
}) {
  let status: RowStatus = 'empty';
  if (uploaded) status = 'uploaded';
  else if (problems > 0) status = 'error';
  else if (ready === count) status = 'ok';
  else if (chosen > 0) status = 'partial';

  let detail = 'PDF de hasta 2 páginas o foto, una por mes';
  if (status === 'uploaded') detail = 'Enviadas';
  else if (status === 'error') detail = `${ready} de ${count} listas · revisa ${problems === 1 ? 'la marcada' : 'las marcadas'}`;
  else if (status !== 'empty') detail = `${ready} de ${count} listas`;

  let icon = fileTextIcon({ size: 20 });
  if (uploading) icon = <CircularProgress size={18} sx={{ color: plr.accent }} />;
  else if (status === 'ok' || status === 'uploaded') icon = checkCircleIcon({ size: 20, strokeWidth: 2.2 });
  else if (status === 'error') icon = alertCircleIcon({ size: 20, strokeWidth: 2.2 });
  const iconColor = {
    empty: plr.accent, partial: plr.accent, error: plr.danger, ok: '#1E7B4F', uploaded: '#1E7B4F',
  }[status];
  const busy = uploading || uploaded;
  const actionLabel = chosen ? 'Revisar' : 'Elegir';

  return (
    <Box
      data-pw="document-card-liquidaciones"
      data-status={status}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: '10px', sm: '12px' },
        p: { xs: '12px', sm: '14px 14px 14px 16px' },
        borderRadius: '16px',
        background: '#FFFFFF',
        boxShadow: `inset 0 0 0 1px ${status === 'error' ? '#E8B4B4' : plr.border}`,
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          borderRadius: '12px',
          background: status === 'error' ? '#FBEDED' : plr.surface,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box sx={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px',
      }}
      >
        <Box component="span" sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, color: plr.ink }}>
          {`Últimas ${count} liquidaciones`}
        </Box>
        <Box
          component="span"
          role={status === 'error' ? 'alert' : undefined}
          sx={{
            fontSize: 13,
            lineHeight: 1.4,
            color: status === 'error' ? plr.danger : plr.muted,
            fontWeight: status === 'error' ? 600 : 400,
          }}
        >
          {detail}
        </Box>
      </Box>
      {!busy && (
        <ButtonBase
          type="button"
          data-pw="upload-liquidaciones-button"
          onClick={onOpen}
          aria-label={`${actionLabel}: últimas ${count} liquidaciones`}
          sx={chipButtonSx(!chosen)}
        >
          {!chosen && uploadIcon({ size: 16, strokeWidth: 2.2 })}
          {actionLabel}
        </ButtonBase>
      )}
    </Box>
  );
}

export default function UploadDocumentsList(props: UploadDocumentsListProps) {
  const {
    needsSettlement, needsPensionStatement, settlementsRequired, pensionStatementsRequired,
    getDocumentsUrl, onReadyChange, upload, onUploaded, onUploadError, onFirstChoose,
  } = props;
  const [settlements, setSettlements] = React.useState<DocState[]>(
    () => Array.from({ length: needsSettlement ? Math.max(settlementsRequired, 1) : 0 }, emptyDoc),
  );
  const [pension, setPension] = React.useState<DocState>(emptyDoc);
  const [uploading, setUploading] = React.useState(false);
  const chooseTracked = React.useRef(false);

  const updateSettlement = (index: number, patch: Partial<DocState>) => {
    setSettlements((prev) => prev.map((doc, i) => (i === index ? { ...doc, ...patch } : doc)));
  };

  const firstExtension = extension(settlements[0]?.file);
  const mixedFormat = (doc: DocState, index: number) => index > 0 && !!doc.file
    && !!firstExtension && extension(doc.file) !== firstExtension;

  const ready = (!needsSettlement || settlements.every((doc, index) => doc.file && doc.fileOk
    && !mixedFormat(doc, index)))
    && (!needsPensionStatement || (!!pension.file && pension.fileOk));
  React.useEffect(() => onReadyChange(ready), [ready]);

  const handleChoose = () => {
    if (chooseTracked.current) return;
    chooseTracked.current = true;
    if (onFirstChoose) onFirstChoose();
  };

  const uploadAll = async () => {
    setUploading(true);
    try {
      const firstSettlement = settlements[0]?.file;
      const { data } = await getDocumentsUrl(
        needsSettlement ? extension(firstSettlement) : undefined,
        needsPensionStatement ? extension(pension.file) : undefined,
      );
      let failed = false;
      if (needsSettlement && data.liquidacionUrls && data.liquidacionUrls.length > 0) {
        const urls = data.liquidacionUrls;
        const results = await Promise.all(settlements.map(async (doc, index) => {
          try {
            await postAWSDocument(doc.pageRefs, doc.file as File, urls[index]);
            return true;
          } catch (e) {
            return false;
          }
        }));
        setSettlements((prev) => prev.map((doc, i) => ({ ...doc, uploaded: results[i] })));
        failed = results.some((ok) => !ok);
      }
      if (!failed && needsPensionStatement && data.cotizacionUrl && pension.file) {
        const res = await postAWSDocument(pension.pageRefs, pension.file, data.cotizacionUrl);
        if (res.status === 201) setPension((prev) => ({ ...prev, uploaded: true }));
        else failed = true;
      }
      setUploading(false);
      if (failed) {
        analytics.page('PLR - DOCUMENTOS', 'ERROR CARGANDO DOCUMENTOS');
        onUploadError();
        return;
      }
      onUploaded();
    } catch (e) {
      setUploading(false);
      analytics.page('PLR - DOCUMENTOS', 'ERROR CARGANDO DOCUMENTOS');
      onUploadError();
    }
  };

  React.useEffect(() => {
    if (upload && !uploading) uploadAll();
  }, [upload]);

  const many = settlements.length > 1;
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const readyCount = settlements.filter((doc, index) => doc.file && doc.fileOk
    && !mixedFormat(doc, index)).length;
  const chosenCount = settlements.filter((doc) => doc.file).length;
  // Archivo elegido que no quedó listo (error, clave o formato distinto); los que se están
  // leyendo también cuentan hasta que terminen, pero eso dura un instante.
  const problemCount = settlements.filter((doc, index) => doc.file
    && (!doc.fileOk || mixedFormat(doc, index))).length;
  const closeSheet = () => {
    setSheetOpen(false);
    analytics.track('Cerrar Dialog', { initiator: 'usuario' });
  };
  // "Continuar": si falta alguna liquidación, baja hasta la primera pendiente; si están todas,
  // cierra la hoja.
  const continueSheet = () => {
    const cards = Array.from(document.querySelectorAll<HTMLElement>(
      '[data-pw="settlements-dialog"] [data-pw="document-card-liquidacion"]',
    ));
    const pending = cards.find((card) => card.dataset.status !== 'ok');
    if (pending && readyCount < settlements.length) {
      pending.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSheetOpen(false);
  };

  const settlementCards = settlements.map((doc, index) => (
    <DocumentCard
      // eslint-disable-next-line react/no-array-index-key
      key={`liquidacion-${index}`}
      type="liquidacion"
      title={many ? `Liquidación ${pastMonthLabel(index)}` : 'Última liquidación'}
      hint="PDF de hasta 2 páginas o foto"
      maxPages={2}
      formatError={mixedFormat(doc, index) ? MIXED_FORMAT_ERROR : undefined}
      file={doc.file}
      onFileChange={(file) => updateSettlement(index, { file, fileOk: false, pageRefs: [] })}
      onFileOk={(fileOk) => updateSettlement(index, { fileOk })}
      onUnlockedPages={(pageRefs) => updateSettlement(index, { pageRefs })}
      onChooseClick={handleChoose}
      uploading={uploading && !doc.uploaded}
      uploaded={doc.uploaded}
    />
  ));

  return (
    <Box data-pw="preloan-documents-list" sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {many ? (
        <>
          <SettlementsRow
            count={settlements.length}
            ready={readyCount}
            problems={problemCount}
            chosen={chosenCount}
            uploading={uploading && !settlements.every((doc) => doc.uploaded)}
            uploaded={settlements.every((doc) => doc.uploaded)}
            onOpen={() => setSheetOpen(true)}
          />
          <PlrModal
            open={sheetOpen}
            onClose={closeSheet}
            title={`Tus últimas ${settlements.length} liquidaciones`}
            titleId="settlements-dialog-title"
            testId="settlements-dialog"
            width={480}
            keepMounted
            footer={(
              <PlrButton testId="settlements-dialog-continue" onClick={continueSheet}>
                Continuar
              </PlrButton>
            )}
          >
            <Box
              component="p"
              sx={{
                m: '-6px 0 0', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.45, color: plr.body,
              }}
            >
              Una por mes, todas en el mismo formato: PDF de hasta 2 páginas o foto.
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {settlementCards}
            </Box>
          </PlrModal>
        </>
      ) : settlementCards}
      {needsPensionStatement && (
        <DocumentCard
          type="cotizaciones"
          title="Cotizaciones AFP"
          hint={`Últimas ${pensionStatementsRequired} · PDF de hasta 3 páginas o foto`}
          maxPages={3}
          file={pension.file}
          onFileChange={(file) => setPension({ ...emptyDoc(), file })}
          onFileOk={(fileOk) => setPension((prev) => ({ ...prev, fileOk }))}
          onUnlockedPages={(pageRefs) => setPension((prev) => ({ ...prev, pageRefs }))}
          onChooseClick={handleChoose}
          uploading={uploading && !pension.uploaded}
          uploaded={pension.uploaded}
        />
      )}
    </Box>
  );
}

UploadDocumentsList.defaultProps = { onFirstChoose: undefined };
